/**
 * Popup used in TabbedReviewSlider. Relies on GlobalNPopup.
 */
class ReviewContentPopup {
  constructor() {
    document.addEventListener("click", (e) => {
      if (
        e.target.matches(
          ".ReviewsSliderItem__ContinueReading[data-continue-reading]"
        )
      ) {
        this.loadContentsAndOpen(e.target);
      }
    });
  }

  /**
   * @param continueReadingBtn - the [data-continue-reading] element.
   */
  loadContentsAndOpen(continueReadingBtn) {
    const parentReviewItem = continueReadingBtn.closest(".ReviewsSliderItem");
    const htmlEl = document.createElement("div");
    htmlEl.innerHTML = parentReviewItem.innerHTML;

    // Get rid of the newly copied continue reading button
    const copiedBtn = htmlEl.querySelector("[data-continue-reading]");
    htmlEl.removeChild(copiedBtn);

    window.GlobalNPopup.openWithContents(
      "reviews-slider-popup",
      htmlEl.innerHTML
    );
  }
}

class TabbedReviewSlider {
  constructor(container) {
    this.container = container;

    this.nextSwiperBtn = this.container.querySelector(
      ".ReviewsSliderNav__Next"
    );
    this.prevSwiperBtn = this.container.querySelector(
      ".ReviewsSliderNav__Prev"
    );
    this.swiperPaginationEl = this.container.querySelector(
      ".ReviewsSliderPagination"
    );
    this.mobileDropdown = this.container.querySelector(
      ".ReviewsSliderMobileDropdown__Select"
    );

    this.cacheSwipers();

    this.init();

    this.reviewContentPopup = new ReviewContentPopup(this.container);
  }

  cacheSwipers() {
    this.swiperEls = {};

    const allSliderEls = this.container.querySelectorAll(".ReviewsSlider");
    allSliderEls.forEach((sliderEl) => {
      const tabpaneHandle = sliderEl.dataset.tabpaneHandle;
      this.swiperEls[tabpaneHandle] = sliderEl;
    });
  }

  init() {
    this.attachTabListeners();

    // Select initial tab
    const initialTab = this.container.querySelector(
      '.ReviewsSliderTablist__Tab[aria-selected="true"]'
    );
    this.selectCategory(initialTab.dataset.tabForCategory);

    // Update swipers again on load
    window.addEventListener("load", () => {
      Object.values(this.swiperEls).forEach((s) => {
        if (s.swiper) {
          s.swiper.update();
        }
      });
    });

    this.initMobileDropdown();
  }

  /**
   * Initializes a single swiper of reviews.
   */
  initSwiper(swiperContainer) {
    if (!swiperContainer.classList.contains("swiper-container")) {
      return;
    }

    return new Swiper(swiperContainer, {
      slidesPerView: 1.2,
      centeredSlides: true,
      spaceBetween: 10,
      threshold: 5,
      navigation: {
        nextEl: this.nextSwiperBtn,
        prevEl: this.prevSwiperBtn,
      },
      pagination: {
        el: this.swiperPaginationEl,
        type: "bullets",
        clickable: true,
      },
      breakpoints: {
        750: {
          slidesPerView: 2,
        },
        1107: {
          initialSlide: 1,
          slidesPerView: 3,
          spaceBetween: 30,
        },
      },
    });
  }

  initMobileDropdown() {
    this.mobileDropdown.addEventListener("change", (e) => {
      this.selectCategory(this.mobileDropdown.value);
    });
  }

  /**
   * Add click functionality that switches tab content panes.
   */
  attachTabListeners() {
    const tabs = this.container.querySelectorAll(".ReviewsSliderTablist__Tab");
    tabs.forEach((tab) => {
      tab.addEventListener("click", () => {
        this.selectCategory(tab.dataset.tabForCategory);
      });
    });
  }

  /**
   * Sets aria-selected on the selected tab, and reveals the corresponding
   * tabpane (which is a swiper of products).
   */
  selectTab(categoryHandle) {
    // Early return if already selected
    const selectedTab = this.container.querySelector(
      `[data-tab-for-category="${categoryHandle}"]`
    );
    if (selectedTab.getAttribute("aria-selected") === "true") {
      return;
    }

    const tabs = this.container.querySelectorAll(".ReviewsSliderTablist__Tab");

    // Set aria-selected
    tabs.forEach((t) => {
      t.setAttribute("aria-selected", "false");
    });
    selectedTab.setAttribute("aria-selected", "true");
  }

  updateMobileDropdown(categoryHandle) {
    if (this.mobileDropdown.value === categoryHandle) {
      return;
    }

    this.mobileDropdown.value = categoryHandle;
  }

  selectCategory(categoryHandle) {
    this.selectTab(categoryHandle);
    this.updateMobileDropdown(categoryHandle);

    // Reveal target tabpane
    const allTabpanes = this.container.querySelectorAll(".ReviewsSlider");
    allTabpanes.forEach((tabpane) => {
      if (tabpane.dataset.tabpaneHandle === categoryHandle) {
        this.showTabpane(tabpane);
      } else {
        this.hideTabpane(tabpane);
      }
    });

    // Refresh swiper.
    // First destroy all swipers except this one, so that the nav is accurate
    Object.values(this.swiperEls).forEach((s) => {
      if (s.swiper) {
        s.swiper.destroy(false, false);
      }
    });
    const relevantSwiperEl = this.swiperEls[categoryHandle];
    this.initSwiper(relevantSwiperEl);
  }

  hideTabpane(tabpane) {
    tabpane.classList.add("ReviewsSlider--hidden");
  }

  showTabpane(tabpane) {
    tabpane.classList.remove("ReviewsSlider--hidden");
  }
}

new TabbedReviewSlider(
  document.querySelector('[data-section-type="tabbed-reviews-slider"]')
);
